import React, { useState } from 'react';
import {
  Card,
  Col,
  Row,
  Typography,
  Input,
  Button,
  Alert,
  Divider,
  Space,
  Tag,
} from "antd";
import {
  FileTextOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import DividendForm from './DividendForm';
import TransactionForm from './TransactionForm';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const TextParserForm = ({ onSuccess, onCancel }) => {
  const [inputText, setInputText] = useState('');
  const [parsedData, setParsedData] = useState(null);
  const [parseError, setParseError] = useState('');
  const [dataType, setDataType] = useState(null); // 'dividend' or 'transaction'
  const [showForm, setShowForm] = useState(false);

  // 분배금 패턴 파싱 함수
  const parseDividendText = (text) => {
    const patterns = {
      // NH투자증권 분배금 패턴
      nh_dividend: {
        account: /계좌번호\s*:\s*([0-9\-\*]+)/,
        stock_name: /종목명\s*:\s*([^\n\r]+)/,
        dividend_amount: /분배금\s*:\s*([0-9,]+)원/,
        dividend_date: /입금일\s*:\s*(\d{4})-(\d{2})-(\d{2})/,
        base_date: /기준일\s*:\s*(\d{4})-(\d{2})-(\d{2})/
      },
      // 한국투자증권 분배금 패턴
      ki_dividend: {
        account: /계좌번호\s*:\s*([0-9\-\*]+)/,
        stock_name: /종목명\s*:\s*([^\n\r]+)/,
        dividend_amount: /입금액\s*:\s*([0-9,]+)원/,
        dividend_date: /입금일자\s*:\s*(\d{4})년(\d{2})월(\d{2})일/
      }
    };

    // NH투자증권 패턴 시도
    if (text.includes('NH투자증권') && text.includes('분배금')) {
      const pattern = patterns.nh_dividend;
      const accountMatch = text.match(pattern.account);
      const stockMatch = text.match(pattern.stock_name);
      const amountMatch = text.match(pattern.dividend_amount);
      const dateMatch = text.match(pattern.dividend_date);

      if (accountMatch && stockMatch && amountMatch && dateMatch) {
        return {
          account_name: accountMatch[1],
          stock_name: stockMatch[1].trim(),
          dividend_amount: parseFloat(amountMatch[1].replace(/,/g, '')),
          dividend_date: `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`,
          quantity: 1 // 기본값, 사용자가 수정 가능
        };
      }
    }

    // 한국투자증권 패턴 시도
    if (text.includes('한국투자') && text.includes('분배금')) {
      const pattern = patterns.ki_dividend;
      const accountMatch = text.match(pattern.account);
      const stockMatch = text.match(pattern.stock_name);
      const amountMatch = text.match(pattern.dividend_amount);
      const dateMatch = text.match(pattern.dividend_date);

      if (accountMatch && stockMatch && amountMatch && dateMatch) {
        return {
          account_name: accountMatch[1],
          stock_name: stockMatch[1].trim(),
          dividend_amount: parseFloat(amountMatch[1].replace(/,/g, '')),
          dividend_date: `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`,
          quantity: 1 // 기본값, 사용자가 수정 가능
        };
      }
    }

    return null;
  };

  // 거래 체결 패턴 파싱 함수
  const parseTransactionText = (text) => {
    const transactions = [];

    // NH투자증권 체결 패턴
    if (text.includes('NH투자증권') && text.includes('체결')) {
      const accountMatch = text.match(/(\d{3}-\d{2}-\d{2}\*+\d+)/);
      const account_name = accountMatch ? accountMatch[1] : '';

      // 각 거래 라인 파싱
      const lines = text.split('\n');
      for (const line of lines) {
        const trimmedLine = line.trim();
        if (trimmedLine && !trimmedLine.includes('NH투자증권') && !trimmedLine.includes('고객님') && !trimmedLine.includes('체결내역')) {
          // "종목명 매수/매도:수량주" 패턴
          const transactionMatch = trimmedLine.match(/^(.+?)\s+(매수|매도):(\d+)주$/);
          if (transactionMatch) {
            transactions.push({
              account_name: account_name,
              stock_name: transactionMatch[1].trim(),
              transaction_type: transactionMatch[2] === '매수' ? 'BUY' : 'SELL',
              quantity: parseInt(transactionMatch[3]),
              price: 0, // 가격 정보가 없으므로 사용자가 입력해야 함
              transaction_date: new Date().toISOString().split('T')[0] // 오늘 날짜로 기본 설정
            });
          }
        }
      }
    }

    // 한국투자증권 체결 패턴
    if (text.includes('한국투자증권') && text.includes('체결')) {
      const accountMatch = text.match(/계좌번호:([0-9\-\*]+)/);
      const account_name = accountMatch ? accountMatch[1] : '';
      
      const stockMatch = text.match(/종목명:([^\(]+)/);
      const typeMatch = text.match(/매매구분:([^\n]+)/);
      const quantityMatch = text.match(/체결수량:(\d+)주/);
      const priceMatch = text.match(/체결단가:([0-9,]+)원/);

      if (stockMatch && typeMatch && quantityMatch && priceMatch) {
        const transactionType = typeMatch[1].includes('매수') ? 'BUY' : 'SELL';
        transactions.push({
          account_name: account_name,
          stock_name: stockMatch[1].trim(),
          transaction_type: transactionType,
          quantity: parseInt(quantityMatch[1]),
          price: parseFloat(priceMatch[1].replace(/,/g, '')),
          transaction_date: new Date().toISOString().split('T')[0] // 오늘 날짜로 기본 설정
        });
      }
    }

    return transactions.length > 0 ? transactions : null;
  };

  const handleParseText = () => {
    if (!inputText.trim()) {
      setParseError('Please enter text to parse');
      return;
    }

    setParseError('');
    setParsedData(null);
    setDataType(null);

    // 분배금 파싱 시도
    const dividendData = parseDividendText(inputText);
    if (dividendData) {
      setParsedData(dividendData);
      setDataType('dividend');
      return;
    }

    // 거래 체결 파싱 시도
    const transactionData = parseTransactionText(inputText);
    if (transactionData) {
      setParsedData(transactionData);
      setDataType('transaction');
      return;
    }

    setParseError('Could not parse the text. Please check the format and try again.');
  };

  const handleProceedToForm = () => {
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setInputText('');
    setParsedData(null);
    setDataType(null);
    if (onSuccess) onSuccess();
  };

  const handleFormCancel = () => {
    setShowForm(false);
  };

  if (showForm && parsedData) {
    if (dataType === 'dividend') {
      return (
        <DividendForm 
          initialData={parsedData}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      );
    } else if (dataType === 'transaction') {
      // 여러 거래가 있는 경우 첫 번째 거래만 처리 (추후 개선 가능)
      const firstTransaction = Array.isArray(parsedData) ? parsedData[0] : parsedData;
      return (
        <TransactionForm 
          initialData={firstTransaction}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      );
    }
  }

  return (
    <Row gutter={[24, 0]} justify="center">
      <Col xs={24} sm={20} md={16} lg={14} xl={12}>
        <Card bordered={false} className="criclebox">
          {/* Header */}
          <div className="number" style={{ marginBottom: '24px' }}>
            <Row align="middle">
              <Col xs={3}>
                <div className="icon-box" style={{ backgroundColor: "#722ed1" }}>
                  <FileTextOutlined style={{ fontSize: '24px', color: '#fff' }} />
                </div>
              </Col>
              <Col xs={21}>
                <span style={{ fontSize: '14px', color: '#666' }}>Portfolio Management</span>
                <Title level={3} style={{ margin: 0 }}>
                  Smart Text Parser
                </Title>
                <Text style={{ color: '#666' }}>
                  Paste your brokerage notification text and let AI parse it automatically
                </Text>
              </Col>
            </Row>
          </div>

          <Divider />

          {/* Instructions */}
          <Alert
            message="How to use"
            description={
              <div>
                <Paragraph style={{ marginBottom: '8px' }}>
                  Simply paste the entire text from your brokerage notification (NH투자증권, 한국투자증권) and click Parse.
                </Paragraph>
                <Paragraph style={{ marginBottom: '0' }}>
                  Supported formats: Dividend notifications and Transaction execution notifications
                </Paragraph>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: '24px' }}
          />

          {/* Text Input */}
          <div style={{ marginBottom: '24px' }}>
            <Title level={5} style={{ marginBottom: '12px' }}>
              Paste Notification Text
            </Title>
            <TextArea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Paste your brokerage notification text here...

Example:
[NH투자증권]퇴직연금 분배금 입금안내

계좌번호 : 800-02-21***5
조*원님 계좌로 분배금 입금안내 드립니다.
1. 종목명 : KODEX 미국서학개미
2. 기준일 : 2025-07-31
3. 분배금 : 3,341원
4. 입금일 : 2025-08-04"
              rows={8}
              style={{ fontSize: '14px' }}
            />
          </div>

          {/* Parse Button */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <Button
              type="primary"
              size="large"
              icon={<ThunderboltOutlined />}
              onClick={handleParseText}
              disabled={!inputText.trim()}
              style={{ minWidth: '200px' }}
            >
              Parse Text
            </Button>
          </div>

          {/* Error Display */}
          {parseError && (
            <Alert
              message="Parse Error"
              description={parseError}
              type="error"
              showIcon
              style={{ marginBottom: '24px' }}
            />
          )}

          {/* Parsed Data Display */}
          {parsedData && (
            <div style={{ marginBottom: '24px' }}>
              <Alert
                message="Parse Successful!"
                description={`Detected ${dataType === 'dividend' ? 'dividend payment' : 'transaction execution'} data`}
                type="success"
                showIcon
                style={{ marginBottom: '16px' }}
              />

              <Card size="small" style={{ backgroundColor: '#f6f8fa' }}>
                <Title level={5} style={{ marginBottom: '16px' }}>
                  <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                  Parsed Data Preview
                </Title>

                {dataType === 'dividend' ? (
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div><Tag color="blue">Account</Tag> {parsedData.account_name}</div>
                    <div><Tag color="green">Stock</Tag> {parsedData.stock_name}</div>
                    <div><Tag color="orange">Amount</Tag> {parsedData.dividend_amount?.toLocaleString()} KRW</div>
                    <div><Tag color="purple">Date</Tag> {parsedData.dividend_date}</div>
                    <div><Tag color="cyan">Quantity</Tag> {parsedData.quantity} shares</div>
                  </Space>
                ) : (
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    {Array.isArray(parsedData) ? parsedData.map((transaction, index) => (
                      <div key={index} style={{ padding: '8px', border: '1px solid #d9d9d9', borderRadius: '4px', marginBottom: '8px' }}>
                        <div><Tag color="blue">Account</Tag> {transaction.account_name}</div>
                        <div><Tag color="green">Stock</Tag> {transaction.stock_name}</div>
                        <div><Tag color="red">Type</Tag> {transaction.transaction_type}</div>
                        <div><Tag color="orange">Quantity</Tag> {transaction.quantity} shares</div>
                        {transaction.price > 0 && <div><Tag color="purple">Price</Tag> {transaction.price?.toLocaleString()} KRW</div>}
                        <div><Tag color="cyan">Date</Tag> {transaction.transaction_date}</div>
                      </div>
                    )) : (
                      <>
                        <div><Tag color="blue">Account</Tag> {parsedData.account_name}</div>
                        <div><Tag color="green">Stock</Tag> {parsedData.stock_name}</div>
                        <div><Tag color="red">Type</Tag> {parsedData.transaction_type}</div>
                        <div><Tag color="orange">Quantity</Tag> {parsedData.quantity} shares</div>
                        {parsedData.price > 0 && <div><Tag color="purple">Price</Tag> {parsedData.price?.toLocaleString()} KRW</div>}
                        <div><Tag color="cyan">Date</Tag> {parsedData.transaction_date}</div>
                      </>
                    )}
                  </Space>
                )}

                {Array.isArray(parsedData) && parsedData.length > 1 && (
                  <Alert
                    message="Multiple Transactions Detected"
                    description={`Found ${parsedData.length} transactions. Currently showing the first one. Multiple transaction processing will be available in future updates.`}
                    type="warning"
                    showIcon
                    style={{ marginTop: '16px' }}
                  />
                )}
              </Card>

              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <Space>
                  <Button
                    type="primary"
                    size="large"
                    icon={<CheckCircleOutlined />}
                    onClick={handleProceedToForm}
                  >
                    Proceed to Form
                  </Button>
                  <Button
                    size="large"
                    onClick={() => {
                      setParsedData(null);
                      setDataType(null);
                      setParseError('');
                    }}
                  >
                    Parse Again
                  </Button>
                </Space>
              </div>
            </div>
          )}

          {/* Cancel Button */}
          <div style={{ textAlign: 'center' }}>
            <Button onClick={onCancel}>
              Back to Data Entry
            </Button>
          </div>
        </Card>
      </Col>
    </Row>
  );
};

export default TextParserForm;
